import type { PortalApiClient } from "./api-client.js";
import { giveawayCountdown, renderGiveawayCard, renderGiveawayManagementState, type GiveawaySummary } from "./giveaway-view.js";

export function createGiveawayController(api: PortalApiClient, actor: () => { userId: string; roleIds: string[] }) {
  let giveaway: GiveawaySummary | null = null;
  let serverOffset = 0;
  let lastSuccessfulRead = 0;
  let busy = false;
  let refreshing = false;
  let started = false;
  let error = "";
  let entryRevision = 0;
  const now = () => Date.now() + serverOffset;
  const render = () => {
    const rail = document.querySelector<HTMLElement>("[data-portal-giveaway]");
    if (rail) {
      rail.hidden = !giveaway;
      if (!giveaway) rail.innerHTML = "";
      else {
        const stale = Date.now() - lastSuccessfulRead > 90_000;
        const closed = giveaway.status !== "open" || Date.parse(giveaway.closesAt) <= now();
        const key = JSON.stringify({ ...giveaway, eligibility: { ...giveaway.eligibility, checkedAt: "" }, busy, error, stale, closed });
        if (rail.dataset.renderKey !== key) {
          const rulesOpen = rail.querySelector<HTMLDetailsElement>("details")?.open;
          const focusedAction = document.activeElement instanceof HTMLElement && rail.contains(document.activeElement) ? document.activeElement.dataset.portalAction : undefined;
          rail.innerHTML = renderGiveawayCard(giveaway, { now: now(), busy, error, stale });
          rail.dataset.renderKey = key;
          const details = rail.querySelector<HTMLDetailsElement>("details");
          if (details && rulesOpen) details.open = true;
          if (focusedAction) rail.querySelector<HTMLElement>(`[data-portal-action="${focusedAction}"]`)?.focus({ preventScroll: true });
        }
        const countdown = rail.querySelector<HTMLElement>("[data-giveaway-countdown]");
        if (countdown && giveaway.status === "open") countdown.textContent = giveawayCountdown(giveaway.closesAt, now());
      }
    }
    const manager = document.querySelector<HTMLElement>("[data-giveaway-manager-current]");
    if (manager) {
      const key = JSON.stringify(giveaway && { id: giveaway.id, status: giveaway.status, count: giveaway.entryCount });
      if (manager.dataset.renderKey !== key) {
        const reason = manager.querySelector<HTMLInputElement>("[data-giveaway-cancel-reason]")?.value;
        manager.innerHTML = renderGiveawayManagementState(giveaway);
        const input = manager.querySelector<HTMLInputElement>("[data-giveaway-cancel-reason]");
        if (input && reason) input.value = reason;
        manager.dataset.renderKey = key;
      }
    }
  };
  const refresh = async () => {
    if (refreshing || !actor().userId) return;
    refreshing = true;
    const revision = entryRevision;
    try {
      const result = await api.getGiveaway(actor().userId);
      if (revision === entryRevision) giveaway = result.giveaway;
      serverOffset = Date.parse(result.serverNow) - Date.now();
      lastSuccessfulRead = Date.now();
    } catch { /* Keep the accepted ticket visible; entry still requires a server response. */ }
    finally { refreshing = false; render(); }
  };
  return {
    render, refresh,
    start() {
      if (started) return;
      started = true;
      void refresh();
      window.setInterval(() => { if (!document.hidden) void refresh(); }, 15_000);
      window.setInterval(() => { if (!document.hidden && giveaway) render(); }, 1000);
      document.addEventListener("visibilitychange", () => { if (!document.hidden) void refresh(); });
    },
    async enter(id: string) {
      if (busy) return;
      busy = true; error = ""; render();
      try {
        const result = await api.enterGiveaway(id, actor().userId);
        entryRevision++;
        if (giveaway?.id === id) {
          giveaway.entry = result.entry;
          if (!result.alreadyEntered) giveaway.entryCount++;
        }
        await refresh();
        return true;
      } catch (cause) {
        error = cause instanceof Error ? cause.message : "Entry could not be confirmed. Please try again.";
        await refresh();
        return false;
      } finally { busy = false; render(); }
    },
    async publish() {
      const form = document.querySelector<HTMLElement>("[data-giveaway-form]");
      if (!form) return;
      const value = (name: string) => form.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[data-giveaway-${name}]`)?.value.trim() ?? "";
      const title = value("title"), prize = value("prize"), description = value("description"), closes = new Date(value("closes"));
      if (title.length < 3 || prize.length < 3 || description.length < 3 || !Number.isFinite(closes.getTime()) || closes.getTime() <= now()) throw new Error("Add a title, prize, message and future closing time.");
      const signature = JSON.stringify({ title, prize, description, closes: closes.toISOString() });
      if (form.dataset.payloadSignature !== signature) { form.dataset.payloadSignature = signature; form.dataset.requestId = crypto.randomUUID(); }
      const { userId, roleIds } = actor();
      await api.publishGiveaway({ requestId: form.dataset.requestId!, title, prize, description, closesAt: closes.toISOString(), actorUserId: userId, actorDiscordRoleIds: roleIds });
      await refresh();
    },
    async archive(id: string) {
      const { userId, roleIds } = actor();
      await api.archiveGiveaway(id, { actorUserId: userId, actorDiscordRoleIds: roleIds });
      entryRevision++;
      giveaway = null;
      render();
      await refresh();
    },
    async cancel(id: string) {
      const reason = document.querySelector<HTMLInputElement>("[data-giveaway-cancel-reason]")?.value.trim() ?? "";
      if (reason.length < 5) throw new Error("Enter a clear cancellation reason for players.");
      const { userId, roleIds } = actor();
      await api.cancelGiveaway(id, { reason, actorUserId: userId, actorDiscordRoleIds: roleIds });
      await refresh();
    }
  };
}
